"use client";

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate, useSearchParams, Link } from 'react-router-dom';
import { DefaultPageLayout } from '../ui/layouts/DefaultPageLayout';
import { LoadingSpinner } from '../components/ui/LoadingSpinner';
import { Button } from '../ui/components/Button';
import { Checkbox } from '../ui/components/Checkbox';
import { useAuthStore } from '../store/authStore';
import { useNotificationStore } from '../store/notificationStore';
import {
  getProjectManageDashboard,
  getProjectTeam,
  addProjectMember,
  removeProjectMember,
  getProjectAuditLog,
  type ProjectDashboard,
  type TeamMember,
  type AuditLogEntry,
} from '../api/creator';
import {
  getAllAnnouncements,
  getAnnouncementStats,
  createAnnouncement,
  type ProjectAnnouncementFull,
  type AnnouncementStats,
} from '../api/projects';
import { OwnerRewardsPanel } from '../components/rewards/OwnerRewardsPanel';
import { OwnerDistributionPanel } from '../components/rewards/OwnerDistributionPanel';
import { featureFlags } from '../config/featureFlags';
import {
  FeatherArrowLeft,
  FeatherSettings,
  FeatherUsers,
  FeatherMessageCircle,
  FeatherFileText,
  FeatherTrash2,
  FeatherUserPlus,
  FeatherExternalLink,
  FeatherStar,
  FeatherSend,
  FeatherChevronRight,
} from '@subframe/core';

type TabId = 'overview' | 'announcements' | 'rewards' | 'team' | 'audit' | 'settings';

interface Tab {
  id: TabId;
  label: string;
  icon: React.ReactNode;
  ownerOnly?: boolean;
  featureFlag?: string;
}

const ALL_TABS: Tab[] = [
  { id: 'overview', label: 'Overview', icon: <FeatherFileText className="h-4 w-4" /> },
  { id: 'announcements', label: 'Announcements', icon: <FeatherMessageCircle className="h-4 w-4" /> },
  { id: 'rewards', label: 'Rewards', icon: <FeatherStar className="h-4 w-4" />, featureFlag: 'project_rewards' },
  { id: 'team', label: 'Team', icon: <FeatherUsers className="h-4 w-4" />, ownerOnly: true },
  { id: 'audit', label: 'Audit Log', icon: <FeatherFileText className="h-4 w-4" /> },
  { id: 'settings', label: 'Settings', icon: <FeatherSettings className="h-4 w-4" /> },
];

export const ProjectManagePage: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { isAuthenticated } = useAuthStore();
  const { success: showSuccess, error: showError } = useNotificationStore();

  // Dashboard state
  const [loading, setLoading] = useState(true);
  const [dashboard, setDashboard] = useState<ProjectDashboard | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Tab state from URL
  const activeTab: TabId = (searchParams.get('tab') as TabId) || 'overview';

  const setActiveTab = useCallback((tab: TabId) => {
    const params = new URLSearchParams(searchParams);
    if (tab === 'overview') {
      params.delete('tab');
    } else {
      params.set('tab', tab);
    }
    setSearchParams(params, { replace: true });
  }, [searchParams, setSearchParams]);

  // Announcement state
  const [allAnnouncements, setAllAnnouncements] = useState<ProjectAnnouncementFull[]>([]);
  const [announcementStats, setAnnouncementStats] = useState<AnnouncementStats | null>(null);
  const [announcementsLoading, setAnnouncementsLoading] = useState(false);
  const [announcementContent, setAnnouncementContent] = useState('');
  const [announcementIsPublic, setAnnouncementIsPublic] = useState(true);
  const [sendingAnnouncement, setSendingAnnouncement] = useState(false);
  const [sendError, setSendError] = useState<string | null>(null);
  const [sendSuccess, setSendSuccess] = useState(false);

  // Team state
  const [team, setTeam] = useState<TeamMember[]>([]);
  const [teamLoading, setTeamLoading] = useState(false);
  const [newMemberAddress, setNewMemberAddress] = useState('');
  const [addingMember, setAddingMember] = useState(false);

  // Audit log state
  const [auditLog, setAuditLog] = useState<AuditLogEntry[]>([]);
  const [auditLoading, setAuditLoading] = useState(false);
  const [auditTotal, setAuditTotal] = useState(0);

  // Derived state
  const isOwner = dashboard?.role === 'owner';
  const enabledFeatures = dashboard?.project.enabled_features || [];

  const isRewardsEnabled = useMemo(() => {
    return enabledFeatures.includes('project_rewards');
  }, [enabledFeatures]);

  const filteredTabs = useMemo(() => {
    return ALL_TABS.filter((tab) => {
      if (tab.ownerOnly && !isOwner) return false;
      if (tab.featureFlag && !enabledFeatures.includes(tab.featureFlag)) return false;
      return true;
    });
  }, [isOwner, enabledFeatures]);

  // Load dashboard data
  const loadDashboard = useCallback(async () => {
    if (!slug || !isAuthenticated) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const data = await getProjectManageDashboard(slug);
      setDashboard(data);
    } catch (err) {
      console.error('Error loading project dashboard:', err);
      setError('Failed to load project. You may not have access to manage this project.');
    } finally {
      setLoading(false);
    }
  }, [slug, isAuthenticated]);

  useEffect(() => {
    loadDashboard();
  }, [loadDashboard]);

  // Load announcements when tab changes
  const loadAnnouncements = useCallback(async () => {
    if (!slug) return;

    setAnnouncementsLoading(true);
    try {
      const [allResult, stats] = await Promise.all([
        getAllAnnouncements(slug),
        getAnnouncementStats(slug),
      ]);
      setAllAnnouncements(allResult.announcements);
      setAnnouncementStats(stats);
    } catch (err) {
      console.error('Error loading announcements:', err);
      showError('Failed to load announcements');
    } finally {
      setAnnouncementsLoading(false);
    }
  }, [slug, showError]);

  useEffect(() => {
    if (activeTab === 'announcements' && dashboard) {
      loadAnnouncements();
    }
  }, [activeTab, dashboard, loadAnnouncements]);

  // Load team when tab changes
  const loadTeam = useCallback(async () => {
    if (!slug) return;

    setTeamLoading(true);
    try {
      const teamData = await getProjectTeam(slug);
      setTeam(teamData);
    } catch (err) {
      console.error('Error loading team:', err);
      showError('Failed to load team members');
    } finally {
      setTeamLoading(false);
    }
  }, [slug, showError]);

  useEffect(() => {
    if (activeTab === 'team' && dashboard) {
      loadTeam();
    }
  }, [activeTab, dashboard, loadTeam]);

  // Load audit log when tab changes
  const loadAuditLog = useCallback(async () => {
    if (!slug) return;

    setAuditLoading(true);
    try {
      const data = await getProjectAuditLog(slug, 50, 0);
      setAuditLog(data.entries);
      setAuditTotal(data.total);
    } catch (err) {
      console.error('Error loading audit log:', err);
      showError('Failed to load audit log');
    } finally {
      setAuditLoading(false);
    }
  }, [slug, showError]);

  useEffect(() => {
    if (activeTab === 'audit' && dashboard) {
      loadAuditLog();
    }
  }, [activeTab, dashboard, loadAuditLog]);

  // Load settings data (holder stats) when tab changes
  const loadSettings = useCallback(async () => {
    if (!slug) return;

    // Reuse announcementStats if already loaded
    if (announcementStats) return;

    setAnnouncementsLoading(true);
    try {
      const stats = await getAnnouncementStats(slug);
      setAnnouncementStats(stats);
    } catch (err) {
      console.error('Error loading stats:', err);
    } finally {
      setAnnouncementsLoading(false);
    }
  }, [slug, announcementStats]);

  useEffect(() => {
    if ((activeTab === 'overview' || activeTab === 'settings') && dashboard) {
      loadSettings();
    }
  }, [activeTab, dashboard, loadSettings]);

  // Handle sending announcement
  const handleSendAnnouncement = async () => {
    if (!slug || !announcementContent.trim()) return;

    setSendingAnnouncement(true);
    setSendError(null);
    setSendSuccess(false);

    try {
      await createAnnouncement(slug, announcementContent.trim(), announcementIsPublic);
      setSendSuccess(true);
      setAnnouncementContent('');

      // Refresh announcements and stats
      const [allResult, stats] = await Promise.all([
        getAllAnnouncements(slug),
        getAnnouncementStats(slug),
      ]);
      setAllAnnouncements(allResult.announcements);
      setAnnouncementStats(stats);
    } catch (err) {
      setSendError(err instanceof Error ? err.message : 'Failed to send announcement');
    } finally {
      setSendingAnnouncement(false);
    }
  };

  // Add team member
  const handleAddMember = async () => {
    if (!slug || !newMemberAddress || addingMember) return;

    setAddingMember(true);
    try {
      await addProjectMember(slug, newMemberAddress);
      showSuccess('Team member added successfully');
      setNewMemberAddress('');
      await loadTeam();
    } catch (err) {
      console.error('Error adding member:', err);
      showError(err instanceof Error ? err.message : 'Failed to add team member');
    } finally {
      setAddingMember(false);
    }
  };

  // Remove team member
  const handleRemoveMember = async (userId: string, username: string | null) => {
    if (!slug) return;

    if (!window.confirm(`Are you sure you want to remove ${username || 'this member'}?`)) {
      return;
    }

    try {
      await removeProjectMember(slug, userId);
      showSuccess('Team member removed');
      await loadTeam();
    } catch (err) {
      console.error('Error removing member:', err);
      showError('Failed to remove team member');
    }
  };

  // Format helpers
  const formatDate = (dateStr: string): string => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatRelativeTime = (dateStr: string): string => {
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  const formatAddress = (addr: string): string =>
    addr ? `${addr.slice(0, 8)}...${addr.slice(-6)}` : '';

  const formatPrice = (price: number | null | undefined): string => {
    if (price === null || price === undefined) return '-';
    const num = typeof price === 'string' ? parseFloat(price) : price;
    if (isNaN(num)) return '-';
    if (num >= 1000000) return `${(num / 1000000).toFixed(2)}M`;
    if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
    return num.toFixed(2);
  };

  // Get action description for audit log
  const getActionDescription = (action: string): string => {
    const descriptions: Record<string, string> = {
      update_profile: 'Updated profile',
      update_settings: 'Updated settings',
      change_rarity: 'Changed rarity methodology',
      change_points_strategy: 'Changed points strategy',
      change_marketplace: 'Changed preferred marketplace',
      add_member: 'Added team member',
      remove_member: 'Removed team member',
      transfer_ownership: 'Transferred ownership',
      create_tag: 'Created custom tag',
      update_tag: 'Updated custom tag',
      delete_tag: 'Deleted custom tag',
      bulk_update_tags: 'Bulk updated tags',
      set_points_override: 'Set point override',
      clear_points_override: 'Cleared point override',
      bulk_set_points: 'Bulk updated points',
      send_announcement: 'Sent announcement',
    };
    return descriptions[action] || action;
  };

  // Not authenticated
  if (!isAuthenticated) {
    return (
      <DefaultPageLayout>
        <div className="flex w-full flex-col items-center justify-center py-16 gap-4">
          <FeatherSettings className="h-12 w-12 text-neutral-400" />
          <h2 className="text-heading-3 font-heading-3 text-default-font">Sign In Required</h2>
          <p className="text-body font-body text-subtext-color text-center max-w-md">
            Please sign in to manage this project.
          </p>
        </div>
      </DefaultPageLayout>
    );
  }

  // Loading state
  if (loading) {
    return (
      <DefaultPageLayout>
        <div className="flex w-full flex-col items-center justify-center py-16">
          <LoadingSpinner size="lg" />
          <p className="mt-4 text-body font-body text-subtext-color">Loading project...</p>
        </div>
      </DefaultPageLayout>
    );
  }

  // Error state
  if (error || !dashboard) {
    return (
      <DefaultPageLayout>
        <div className="flex w-full flex-col items-center justify-center py-16 gap-4">
          <FeatherSettings className="h-12 w-12 text-neutral-400" />
          <p className="text-body font-body text-error-600">{error || 'Project not found'}</p>
          <button
            onClick={() => navigate('/creator')}
            className="flex items-center gap-2 text-brand-600 hover:text-brand-700"
          >
            <FeatherArrowLeft className="h-4 w-4" />
            Back to Dashboard
          </button>
        </div>
      </DefaultPageLayout>
    );
  }

  return (
    <DefaultPageLayout>
      <div className="flex w-full flex-col items-start gap-6">
        {/* Header */}
        <div className="flex items-center gap-4 w-full">
          <button
            onClick={() => navigate('/creator')}
            className="p-2 rounded-md hover:bg-default-background transition-colors"
          >
            <FeatherArrowLeft className="h-5 w-5 text-neutral-500" />
          </button>
          <div className="flex items-center gap-4 flex-1">
            {dashboard.project.image_url ? (
              <img
                src={dashboard.project.image_url}
                alt={dashboard.project.name}
                className="w-12 h-12 rounded-lg object-cover"
              />
            ) : (
              <div className="w-12 h-12 rounded-lg bg-neutral-100 border border-neutral-border flex items-center justify-center">
                <span className="text-heading-3 font-heading-3 text-neutral-400">
                  {dashboard.project.name.charAt(0).toUpperCase()}
                </span>
              </div>
            )}
            <div>
              <div className="flex items-center gap-2">
                <h1 className="text-heading-2 font-heading-2 text-default-font">
                  {dashboard.project.name}
                </h1>
                <span
                  className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                    isOwner ? 'bg-brand-100 text-brand-700' : 'bg-blue-100 text-blue-700'
                  }`}
                >
                  {isOwner ? 'Owner' : 'Manager'}
                </span>
              </div>
              {dashboard.project.description && (
                <p className="text-caption font-caption text-subtext-color line-clamp-1 max-w-lg">
                  {dashboard.project.description}
                </p>
              )}
            </div>
          </div>
          <Link
            to={`/projects/${dashboard.project.slug}`}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-2 px-3 py-2 rounded-md border border-neutral-border bg-default-background text-subtext-color hover:text-default-font transition-colors"
          >
            <FeatherExternalLink className="h-4 w-4" />
            View Project
          </Link>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 border-b border-neutral-border w-full overflow-x-auto">
          {filteredTabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-3 border-b-2 transition-colors whitespace-nowrap ${
                activeTab === tab.id
                  ? 'border-brand-600 text-brand-600'
                  : 'border-transparent text-subtext-color hover:text-default-font'
              }`}
            >
              {tab.icon}
              {tab.label}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        <div className="w-full">
          {/* ==================== Overview Tab ==================== */}
          {activeTab === 'overview' && (
            <div className="space-y-6">
              {/* Quick Stats */}
              <div className="p-6 rounded-lg border border-neutral-border bg-default-background">
                <h3 className="text-body-bold font-body-bold text-default-font mb-4">Quick Stats</h3>
                <div className="grid grid-cols-4 gap-4 mobile:grid-cols-2">
                  <div>
                    <p className="text-caption font-caption text-subtext-color">Team Members</p>
                    <p className="text-heading-3 font-heading-3 text-default-font">
                      {dashboard.team.count}
                    </p>
                  </div>
                  <div>
                    <p className="text-caption font-caption text-subtext-color">Collections</p>
                    <p className="text-heading-3 font-heading-3 text-default-font">
                      {dashboard.collections.length}
                    </p>
                  </div>
                  <div>
                    <p className="text-caption font-caption text-subtext-color">Total NFT Holders</p>
                    <p className="text-heading-3 font-heading-3 text-default-font">
                      {announcementStats?.total_holders ?? '-'}
                    </p>
                  </div>
                  <div>
                    <p className="text-caption font-caption text-subtext-color">XRP Deals Users</p>
                    <p className="text-heading-3 font-heading-3 text-brand-600">
                      {announcementStats ? `${announcementStats.eligible_users} (${announcementStats.total_holders > 0 ? Math.round((announcementStats.eligible_users / announcementStats.total_holders) * 100) : 0}%)` : '-'}
                    </p>
                  </div>
                </div>
              </div>

              {/* Collections */}
              {dashboard.collections.length > 0 && (
                <div className="p-6 rounded-lg border border-neutral-border bg-default-background">
                  <h3 className="text-body-bold font-body-bold text-default-font mb-4">Collections</h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    {dashboard.collections.map((collection) => (
                      <Link
                        key={collection.id}
                        to={`/creator/projects/${slug}/collections/${collection.slug || collection.id}`}
                        className="group flex items-center gap-3 p-3 rounded-lg border border-neutral-border bg-default-background hover:border-brand-500 hover:bg-neutral-50 dark:hover:bg-white/[0.03] transition-all"
                      >
                        {collection.image_url ? (
                          <img
                            src={collection.image_url}
                            alt={collection.name}
                            className="w-10 h-10 rounded-md object-cover"
                          />
                        ) : (
                          <div className="w-10 h-10 rounded-md bg-neutral-100 flex items-center justify-center">
                            <span className="text-body-bold font-body-bold text-neutral-400">
                              {collection.name.charAt(0).toUpperCase()}
                            </span>
                          </div>
                        )}
                        <div className="flex-1 min-w-0">
                          <p className="text-body-bold font-body-bold text-default-font truncate group-hover:text-brand-600 transition-colors">
                            {collection.name}
                          </p>
                          <p className="text-caption font-caption text-subtext-color">
                            {collection.floor_price != null ? `Floor: ${formatPrice(collection.floor_price)} XRP` : 'No floor'} | Supply: {collection.total_supply}
                          </p>
                        </div>
                        <div className="flex items-center gap-1 shrink-0">
                          <span className="text-caption font-caption text-subtext-color opacity-0 group-hover:opacity-100 transition-opacity">
                            Manage
                          </span>
                          <FeatherChevronRight className="h-4 w-4 text-subtext-color group-hover:text-brand-600 transition-colors" />
                        </div>
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* ==================== Announcements Tab ==================== */}
          {activeTab === 'announcements' && (
            <div className="flex w-full flex-col items-start gap-8">
              {/* Send Announcement Section */}
              <div className="w-full flex flex-col gap-4">
                <h2 className="text-heading-2 font-heading-2 text-default-font">Send Announcement</h2>

                <div className="w-full rounded-lg border border-neutral-border bg-default-background p-6">
                  <div className="flex flex-col gap-4">
                    <div>
                      <label className="block text-body-bold font-body-bold text-default-font mb-2">
                        Message
                      </label>
                      <textarea
                        value={announcementContent}
                        onChange={(e) => setAnnouncementContent(e.target.value.slice(0, 255))}
                        placeholder="Send a message to all holders of any NFT in the project..."
                        rows={4}
                        className="w-full rounded-md border border-neutral-border px-3 py-2 text-body font-body text-default-font bg-default-background placeholder:text-neutral-400 focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500 resize-none"
                        disabled={sendingAnnouncement || (announcementStats !== null && !announcementStats.can_send_today)}
                      />
                      <div className="flex items-center justify-between mt-1">
                        <span className="text-caption font-caption text-subtext-color">
                          {announcementContent.length}/255 characters
                        </span>
                      </div>
                    </div>

                    <label className="flex items-center gap-2 cursor-pointer">
                      <Checkbox
                        checked={announcementIsPublic}
                        onCheckedChange={(checked) => setAnnouncementIsPublic(checked === true)}
                        disabled={sendingAnnouncement}
                      />
                      <span className="text-body font-body text-default-font">
                        Show on public Announcements tab
                      </span>
                    </label>
                    <p className="text-caption font-caption text-subtext-color -mt-2 ml-6">
                      If unchecked, the announcement will only be sent to user inboxes
                    </p>

                    {sendError && (
                      <div className="p-3 rounded-md bg-error-50 text-error-700 text-body font-body">
                        {sendError}
                      </div>
                    )}

                    {sendSuccess && (
                      <div className="p-3 rounded-md bg-success-50 text-success-700 text-body font-body">
                        Announcement sent! It will be delivered to all eligible holders shortly.
                      </div>
                    )}

                    <div className="flex items-center justify-between">
                      <span className="text-caption font-caption text-subtext-color">
                        Limit: 1 announcement per day
                      </span>
                      <Button
                        variant="brand-primary"
                        icon={<FeatherSend />}
                        onClick={handleSendAnnouncement}
                        loading={sendingAnnouncement}
                        disabled={
                          !announcementContent.trim() ||
                          sendingAnnouncement ||
                          (announcementStats !== null && !announcementStats.can_send_today)
                        }
                      >
                        {announcementStats?.can_send_today === false ? 'Already sent today' : 'Send Announcement'}
                      </Button>
                    </div>
                  </div>
                </div>
              </div>

              {/* Announcement History Section */}
              <div className="w-full flex flex-col gap-4">
                <h2 className="text-heading-2 font-heading-2 text-default-font">Announcement History</h2>

                {announcementsLoading ? (
                  <div className="flex w-full items-center justify-center py-8">
                    <LoadingSpinner size="sm" />
                  </div>
                ) : allAnnouncements.length === 0 ? (
                  <div className="flex w-full items-center justify-center rounded-md border border-solid border-neutral-border bg-default-background p-8">
                    <span className="text-body font-body text-subtext-color">
                      No announcements sent yet
                    </span>
                  </div>
                ) : (
                  <div className="w-full overflow-x-auto">
                    <table className="w-full text-left">
                      <thead>
                        <tr className="border-b border-neutral-border">
                          <th className="py-3 px-4 text-caption font-caption text-subtext-color">Date</th>
                          <th className="py-3 px-4 text-caption font-caption text-subtext-color">Content</th>
                          <th className="py-3 px-4 text-caption font-caption text-subtext-color text-center">Status</th>
                          <th className="py-3 px-4 text-caption font-caption text-subtext-color text-center">Visibility</th>
                          <th className="py-3 px-4 text-caption font-caption text-subtext-color text-right">Recipients</th>
                        </tr>
                      </thead>
                      <tbody>
                        {allAnnouncements.map((announcement) => (
                          <tr key={announcement.id} className="border-b border-neutral-100">
                            <td className="py-3 px-4 text-caption font-caption text-subtext-color whitespace-nowrap">
                              {formatRelativeTime(announcement.created_at)}
                            </td>
                            <td className="py-3 px-4 text-body font-body text-default-font max-w-xs truncate">
                              {announcement.content}
                            </td>
                            <td className="py-3 px-4 text-center">
                              <span
                                className={`text-body font-body ${
                                  announcement.status === 'sent'
                                    ? 'text-success-600'
                                    : announcement.status === 'failed'
                                    ? 'text-error-600'
                                    : 'text-warning-600'
                                }`}
                              >
                                {announcement.status}
                              </span>
                            </td>
                            <td className="py-3 px-4 text-center">
                              <span className={`text-body font-body ${announcement.is_public ? 'text-brand-600' : 'text-subtext-color'}`}>
                                {announcement.is_public ? 'Public' : 'Private'}
                              </span>
                            </td>
                            <td className="py-3 px-4 text-body font-body text-default-font text-right">
                              {announcement.status === 'sent' || announcement.status === 'failed'
                                ? (announcement.recipient_count ?? '--')
                                : announcement.eligible_users_at_send != null
                                  ? `0/${announcement.eligible_users_at_send}`
                                  : '--'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* ==================== Rewards Tab ==================== */}
          {activeTab === 'rewards' && (
            <div className="w-full">
              {isRewardsEnabled && slug ? (
                featureFlags.distribution ? (
                  <OwnerDistributionPanel
                    projectSlug={slug}
                    projectCollections={dashboard.collections.map((c) => ({
                      id: c.id,
                      name: c.name,
                    }))}
                  />
                ) : (
                  <OwnerRewardsPanel
                    projectSlug={slug}
                    projectCollections={dashboard.collections.map((c) => ({
                      id: c.id,
                      name: c.name,
                    }))}
                  />
                )
              ) : (
                <div className="flex w-full items-center justify-center rounded-md border border-solid border-neutral-border bg-default-background p-12">
                  <div className="flex flex-col items-center gap-2">
                    <FeatherStar className="w-8 h-8 text-neutral-400" />
                    <span className="text-body font-body text-subtext-color">
                      Rewards are not enabled for this project.
                    </span>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* ==================== Team Tab (Owner Only) ==================== */}
          {activeTab === 'team' && isOwner && (
            <div className="space-y-6">
              {/* Add Member */}
              <div className="p-6 rounded-lg border border-neutral-border bg-default-background">
                <h3 className="text-body-bold font-body-bold text-default-font mb-4">Add Team Member</h3>
                <div className="flex gap-4">
                  <input
                    type="text"
                    value={newMemberAddress}
                    onChange={(e) => setNewMemberAddress(e.target.value)}
                    placeholder="Enter XRPL address (r...)"
                    className="flex-1 px-3 py-2 rounded-md border border-neutral-border bg-default-background text-default-font focus:outline-none focus:ring-2 focus:ring-brand-500"
                  />
                  <button
                    onClick={handleAddMember}
                    disabled={addingMember || !newMemberAddress}
                    className="flex items-center gap-2 px-4 py-2 rounded-md bg-brand-600 text-white hover:bg-brand-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    {addingMember ? (
                      <LoadingSpinner size="sm" />
                    ) : (
                      <FeatherUserPlus className="h-4 w-4" />
                    )}
                    Add Member
                  </button>
                </div>
                <p className="text-caption font-caption text-subtext-color mt-2">
                  The user must have an account on xrp.deals to be added as a team member.
                </p>
              </div>

              {/* Team List */}
              <div className="p-6 rounded-lg border border-neutral-border bg-default-background">
                <h3 className="text-body-bold font-body-bold text-default-font mb-4">Team Members</h3>
                {teamLoading ? (
                  <div className="flex justify-center py-8">
                    <LoadingSpinner size="md" />
                  </div>
                ) : team.length === 0 ? (
                  <p className="text-body font-body text-subtext-color text-center py-8">
                    No team members yet.
                  </p>
                ) : (
                  <div className="space-y-3">
                    {team.map((member) => (
                      <div
                        key={member.id}
                        className="flex items-center justify-between p-4 rounded-lg border border-neutral-border bg-default-background"
                      >
                        <div className="flex items-center gap-3">
                          {member.user?.avatar_url ? (
                            <img
                              src={member.user.avatar_url}
                              alt=""
                              className="w-10 h-10 rounded-full object-cover"
                            />
                          ) : (
                            <div className="w-10 h-10 rounded-full bg-default-background border border-neutral-border flex items-center justify-center">
                              <FeatherUsers className="h-5 w-5 text-neutral-500" />
                            </div>
                          )}
                          <div>
                            <p className="text-body-bold font-body-bold text-default-font">
                              {member.user?.username || (member.user?.xrpl_address ? member.user.xrpl_address.slice(0, 12) + '...' : 'Unknown')}
                            </p>
                            <p className="text-caption font-caption text-subtext-color">
                              {member.user?.xrpl_address ? formatAddress(member.user.xrpl_address) : ''}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          <span
                            className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                              member.role === 'owner'
                                ? 'bg-brand-100 text-brand-700'
                                : 'bg-blue-100 text-blue-700'
                            }`}
                          >
                            {member.role === 'owner' ? 'Owner' : 'Manager'}
                          </span>
                          {member.role !== 'owner' && (
                            <button
                              onClick={() => handleRemoveMember(member.user_id, member.user?.username ?? null)}
                              className="p-2 rounded-md text-red-500 hover:bg-red-50 transition-colors"
                            >
                              <FeatherTrash2 className="h-4 w-4" />
                            </button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* ==================== Audit Log Tab ==================== */}
          {activeTab === 'audit' && (
            <div className="p-6 rounded-lg border border-neutral-border bg-default-background">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-body-bold font-body-bold text-default-font">Activity Log</h3>
                {auditTotal > 0 && (
                  <span className="text-caption font-caption text-subtext-color">
                    {auditTotal} total entries
                  </span>
                )}
              </div>
              {auditLoading ? (
                <div className="flex justify-center py-8">
                  <LoadingSpinner size="md" />
                </div>
              ) : auditLog.length === 0 ? (
                <p className="text-body font-body text-subtext-color text-center py-8">
                  No activity recorded yet.
                </p>
              ) : (
                <div className="space-y-3">
                  {auditLog.map((entry) => (
                    <div
                      key={entry.id}
                      className="flex items-start gap-3 p-3 rounded-lg border border-neutral-border bg-default-background"
                    >
                      <div className="flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <p className="text-body font-body text-default-font">
                            {getActionDescription(entry.action)}
                          </p>
                          <span className="text-caption font-caption text-subtext-color">
                            by {entry.username || (entry.xrpl_address ? entry.xrpl_address.slice(0, 10) + '...' : 'Unknown')}
                          </span>
                          {entry.collection_name && (
                            <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-700">
                              {entry.collection_name}
                            </span>
                          )}
                        </div>
                        {entry.details && Object.keys(entry.details).length > 0 && (
                          <p className="text-caption font-caption text-subtext-color mt-1">
                            {JSON.stringify(entry.details).slice(0, 100)}
                            {JSON.stringify(entry.details).length > 100 && '...'}
                          </p>
                        )}
                      </div>
                      <span className="text-caption font-caption text-subtext-color whitespace-nowrap">
                        {formatDate(entry.created_at)}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* ==================== Settings Tab ==================== */}
          {activeTab === 'settings' && (
            <div className="flex w-full items-center justify-center rounded-md border border-solid border-neutral-border bg-default-background p-12">
              <div className="flex flex-col items-center gap-2">
                <FeatherSettings className="w-8 h-8 text-neutral-400" />
                <span className="text-body-bold font-body-bold text-default-font">
                  Project Settings
                </span>
                <span className="text-body font-body text-subtext-color">
                  Coming soon
                </span>
              </div>
            </div>
          )}
        </div>
      </div>
    </DefaultPageLayout>
  );
};
